<script setup lang="ts">
import { onMounted, ref } from 'vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import ConfirmModal from '@/components/common/layout/ConfirmModal.vue'
import {
  useSourceAdminStore,
  type SourceItem,
} from '@/stores/useSourceAdminStore'
import { PlusIcon, TrashIcon, GlobeAltIcon } from '@heroicons/vue/24/outline'

const store = useSourceAdminStore()

const showFormModal = ref(false)
const editing = ref<SourceItem | null>(null)
const form = ref({ name: '', modelConnector: '', active: true })
const saving = ref(false)
const errorMsg = ref<string | null>(null)

const showDeleteModal = ref(false)
const toDelete = ref<SourceItem | null>(null)
const deleteError = ref<string | null>(null)

function openCreate() {
  editing.value = null
  form.value = { name: '', modelConnector: '', active: true }
  errorMsg.value = null
  showFormModal.value = true
}

function openEdit(s: SourceItem) {
  editing.value = s
  form.value = { name: s.name, modelConnector: s.modelConnector, active: s.active }
  errorMsg.value = null
  showFormModal.value = true
}

function closeFormModal() {
  showFormModal.value = false
  editing.value = null
  errorMsg.value = null
}

async function submit() {
  if (!form.value.name.trim() || !form.value.modelConnector.trim()) {
    errorMsg.value = 'Le nom et le connecteur sont obligatoires'
    return
  }
  saving.value = true
  errorMsg.value = null
  try {
    const payload = {
      name: form.value.name.trim(),
      modelConnector: form.value.modelConnector.trim().toLowerCase(),
      active: form.value.active,
    }
    const res = editing.value
      ? await store.update(editing.value.uuid, payload)
      : await store.create(payload)
    if (res.ok) closeFormModal()
    else errorMsg.value = res.message
  } finally {
    saving.value = false
  }
}

function askDelete(s: SourceItem) {
  toDelete.value = s
  deleteError.value = null
  showDeleteModal.value = true
}

async function confirmDelete() {
  if (!toDelete.value) return
  const res = await store.remove(toDelete.value.uuid)
  if (res.ok) {
    showDeleteModal.value = false
    toDelete.value = null
  } else {
    deleteError.value = res.message
  }
}

function formatDate(s: string | null | undefined) {
  if (!s) return '—'
  return new Date(s).toLocaleString('fr-FR')
}

onMounted(async () => {
  await store.fetchAll()
})
</script>

<template>
  <div class="p-6">
    <Bs2Title title="Sources" subtitle="Connecteurs externes (Odoo, Alphalink, Keyyo, …)" />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>
    </div>

    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <div class="flex items-center justify-between mb-4 gap-2">
          <h2 class="card-title">Sources ({{ store.sources.length }})</h2>
          <div class="flex gap-2">
            <ButtonDaisy text="Actualiser" variant="outline" size="sm" @click="store.fetchAll()" />
            <button class="btn btn-primary btn-sm gap-1" @click="openCreate">
              <PlusIcon class="w-4 h-4" />
              Nouvelle source
            </button>
          </div>
        </div>

        <div v-if="store.isLoading" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>
        <div v-else-if="store.sources.length === 0" class="py-8 text-center text-base-content/60">
          Aucune source.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Connecteur</th>
                <th class="text-center">Actif</th>
                <th class="text-right">Companies</th>
                <th class="text-right">Contacts</th>
                <th class="text-right">Services</th>
                <th class="text-right">Tickets</th>
                <th class="text-right">Devis</th>
                <th class="text-right">Factures</th>
                <th class="text-right">Total</th>
                <th>Mis à jour</th>
                <th class="w-32"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in store.sources" :key="s.uuid" class="hover">
                <td class="font-semibold">{{ s.name }}</td>
                <td class="font-mono text-xs">{{ s.modelConnector }}</td>
                <td class="text-center">
                  <span v-if="s.active" class="badge badge-success badge-sm">Actif</span>
                  <span v-else class="badge badge-ghost badge-sm">Inactif</span>
                </td>
                <td class="text-right font-mono">{{ s.counts.companies }}</td>
                <td class="text-right font-mono">{{ s.counts.contacts }}</td>
                <td class="text-right font-mono">{{ s.counts.services }}</td>
                <td class="text-right font-mono">{{ s.counts.tickets }}</td>
                <td class="text-right font-mono">{{ s.counts.quotes }}</td>
                <td class="text-right font-mono">{{ s.counts.invoices }}</td>
                <td class="text-right font-mono font-bold">{{ s.counts.total }}</td>
                <td class="text-xs text-base-content/60">{{ formatDate(s.updatedAt) }}</td>
                <td>
                  <div class="flex gap-1 justify-end">
                    <button class="btn btn-ghost btn-xs" @click="openEdit(s)">Modifier</button>
                    <button
                      class="btn btn-ghost btn-xs text-error"
                      :disabled="s.counts.total > 0"
                      :title="s.counts.total > 0 ? 'Source utilisée — désactivez-la' : 'Supprimer'"
                      @click="askDelete(s)"
                    >
                      <TrashIcon class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="modal" :class="{ 'modal-open': showFormModal }">
      <div class="modal-box max-w-lg">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <GlobeAltIcon class="w-6 h-6" />
          {{ editing ? `Modifier ${editing.name}` : 'Nouvelle source' }}
        </h3>
        <div class="mt-4 space-y-4">
          <div class="form-control">
            <label class="label"><span class="label-text">Nom</span></label>
            <input
              v-model="form.name"
              type="text"
              class="input input-bordered input-sm"
              placeholder="ALPHALINK"
              :disabled="saving"
            />
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text">Connecteur</span>
              <span class="label-text-alt text-xs text-base-content/60">odoo, alphalink, keyyo, ninjaone, glpi…</span>
            </label>
            <input
              v-model="form.modelConnector"
              type="text"
              class="input input-bordered input-sm font-mono"
              placeholder="alphalink"
              :disabled="saving"
              @input="form.modelConnector = form.modelConnector.toLowerCase()"
            />
          </div>
          <div class="form-control">
            <label class="label cursor-pointer justify-start gap-3">
              <input v-model="form.active" type="checkbox" class="toggle toggle-success toggle-sm" :disabled="saving" />
              <span class="label-text">Source active</span>
            </label>
          </div>
          <div v-if="errorMsg" class="alert alert-error text-sm">{{ errorMsg }}</div>
        </div>
        <div class="modal-action">
          <button class="btn" :disabled="saving" @click="closeFormModal">Annuler</button>
          <button class="btn btn-primary" :disabled="saving" @click="submit">
            {{ saving ? 'Enregistrement…' : (editing ? 'Enregistrer' : 'Créer') }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeFormModal" />
    </div>

    <ConfirmModal
      :show-modal="showDeleteModal"
      title="Supprimer la source"
      :body="`Supprimer « ${toDelete?.name ?? ''} » ?`"
      confirm-text="Supprimer"
      confirm-variant="error"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false; toDelete = null; deleteError = null"
    />
    <div v-if="deleteError" class="toast toast-end">
      <div class="alert alert-error text-sm">{{ deleteError }}</div>
    </div>
  </div>
</template>
